import { Link } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import Layout from "../../../components/layout/Layout.js";
import Dialog from "../../../components/ui/Dialog";

import {
  useSyncConfigs,
  useCreateSyncConfig,
  useUpdateSyncConfig,
  useDeleteSyncConfig,
  useRunSync,
} from "../../../api/queries/adminQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import "./DataSync.css";
import { useCanModify } from "../../../hooks/usePermissions";

// Activate the relativeTime plugin
dayjs.extend(relativeTime);

const ScheduleSync = () => {
  // View-only roles see the list without create / edit / delete
  const canModify = useCanModify("data-tools", { company: true });
  const { data: configs = [] } = useSyncConfigs();
  const createSync = useCreateSyncConfig();
  const updateSync = useUpdateSyncConfig();
  const deleteSync = useDeleteSyncConfig();
  const runSync = useRunSync();
  const status =
    createSync.isPending || updateSync.isPending ? "loading" : "idle";
  // Several configs can run at once, so track "running" per config id.
  const [syncingIds, setSyncingIds] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);

  const initialFormState = {
    name: "",
    companyId: "",
    sourceType: "ZOHO",
    configType: "ZOHO_TEMPLATE",
    cronExpression: "0 2 * * *",
    isActive: true,
    source: {
      identifier: "",
      credentials: {
        clientId: "",
        clientSecret: "",
        refreshToken: "",
        dc: "com",
      },
    },
    process: { targetIdentifier: "" },
  };

  const [formData, setFormData] = useState(initialFormState);
  const [scheduleTime, setScheduleTime] = useState("02:00");
  const [frequency, setFrequency] = useState("daily");

  /**
   * Helper: Translates Cron strings into friendly English
   */
  const formatCronLabel = (cron) => {
    if (!cron) return "Not scheduled";
    const parts = cron.split(" ");

    // Every X minutes logic
    if (parts[0].startsWith("*/")) {
      return `Every ${parts[0].replace("*/", "")} mins`;
    }

    // Time-based (Daily/Weekly)
    if (parts.length >= 5) {
      try {
        const timeStr = dayjs()
          .set("hour", parts[1])
          .set("minute", parts[0])
          .format("hh:mm A");
        if (parts[4] === "0") return `Sundays at ${timeStr}`;
        if (parts[4] === "*") return `Daily at ${timeStr}`;
      } catch (e) {
        return cron;
      }
    }
    return cron;
  };

  // Sync UI inputs (Time/Frequency) to the actual Cron string
  useEffect(() => {
    const [hours, minutes] = scheduleTime.split(":");
    let cron = `${parseInt(minutes)} ${parseInt(hours)} * * *`;
    if (frequency === "weekly")
      cron = `${parseInt(minutes)} ${parseInt(hours)} * * 0`;
    setFormData((prev) => ({ ...prev, cronExpression: cron }));
  }, [scheduleTime, frequency]);

  const handleRunTask = async (id) => {
    try {
      setSyncingIds((prev) => ({ ...prev, [id]: "running" }));
      const result = await runSync.mutateAsync(id);
      toast.success(result.message || "Manual sync started!");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Manual sync failed"));
    } finally {
      setSyncingIds(({ [id]: _done, ...rest }) => rest);
    }
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditId(config.id);
      setFormData({
        ...initialFormState,
        ...config,
        source: {
          ...initialFormState.source,
          ...(config.source || {}),
          credentials: {
            ...initialFormState.source.credentials,
            ...(config.source?.credentials || {}),
          },
        },
        process: config.process || { targetIdentifier: "" },
      });

      const parts = config.cronExpression?.split(" ") || [];
      if (parts.length >= 2 && !parts[0].includes("*")) {
        setScheduleTime(
          `${parts[1].padStart(2, "0")}:${parts[0].padStart(2, "0")}`,
        );
        setFrequency(parts[4] === "0" ? "weekly" : "daily");
      }
    } else {
      setEditId(null);
      setFormData(initialFormState);
      setScheduleTime("02:00");
      setFrequency("daily");
    }
    setShowModal(true);
  };

  const updateCredentials = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      source: {
        ...prev.source,
        credentials: { ...prev.source.credentials, [field]: value },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await updateSync.mutateAsync({ id: editId, dto: formData });
      else await createSync.mutateAsync(formData);
      setShowModal(false);
      toast.success("Configuration saved successfully!");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save configuration"));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Permanently delete this configuration?")) {
      try {
        await deleteSync.mutateAsync(id);
        toast.info("Config removed");
      } catch (err) {
        toast.error(getApiErrorMessage(err, "Failed to delete configuration"));
      }
    }
  };

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Sync Schedules</h1>
            <p className="text-muted small mb-0">
              Scheduled syncs from S3 / Zoho.
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={() => handleOpenModal()}
            >
              <i className="bi bi-plus-lg me-2"></i>New Task
            </button>
          )}
        </div>

        <div className="mb-4">
          <input
            type="text"
            className="form-control border-0 shadow-sm px-4 rounded-10 h-45px"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="glass-container overflow-hidden shadow-sm">
          <table className="table table-hover mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="ps-4 py-3">Config Details</th>
                <th className="py-3 text-center">Schedule</th>
                <th className="py-3 text-center">Last Run</th>
                <th className="pe-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs
                .filter((c) =>
                  c.name.toLowerCase().includes(searchTerm.toLowerCase()),
                )
                .map((config) => (
                  <tr key={config.id} className="align-middle">
                    <td className="ps-4 py-3">
                      <div className="fw-bold text-dark fs-085">
                        {config.name}
                      </div>
                      <div className="text-muted font-monospace fs-065">
                        {config.companyId}
                      </div>
                    </td>
                    <td className="text-center">
                      <div className="schedule-badge">
                        <i className="bi bi-calendar-event me-2"></i>
                        {formatCronLabel(config.cronExpression)}
                      </div>
                    </td>
                    <td className="text-center text-muted fs-075">
                      {config.lastRunAt
                        ? dayjs(config.lastRunAt).fromNow()
                        : "Never"}
                    </td>
                    <td className="pe-4 text-end">
                      {canModify ? (
                        <>
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              className="btn btn-sm btn-outline-dark"
                              onClick={() => handleOpenModal(config)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleRunTask(config.id)}
                              disabled={syncingIds[config.id] === "running"}
                            >
                              {syncingIds[config.id] === "running"
                                ? "Running..."
                                : "Run Now"}
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() => handleDelete(config.id)}
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <Dialog
          title={<>Zoho Configuration</>}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-12">
                <label className="profile-label">General</label>
                <input
                  className="form-control mb-2"
                  placeholder="Task Name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                />
                <input
                  className="form-control font-monospace small"
                  placeholder="Company UUID"
                  value={formData.companyId}
                  onChange={(e) =>
                    setFormData({ ...formData, companyId: e.target.value })
                  }
                  required
                />
              </div>

              <div className="col-md-6">
                <label className="profile-label">Start Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">Frequency</label>
                <select
                  className="form-select"
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly (Sunday)</option>
                </select>
              </div>

              <div className="col-12">
                <div className="p-3 bg-white rounded-3 border">
                  <label className="fw-bold small text-primary text-uppercase mb-2 d-block fs-065">
                    Zoho Credentials
                  </label>
                  <input
                    className="form-control form-control-sm mb-2"
                    placeholder="Client ID"
                    value={formData.source?.credentials?.clientId || ""}
                    onChange={(e) =>
                      updateCredentials("clientId", e.target.value)
                    }
                    required
                  />
                  <input
                    className="form-control form-control-sm mb-2"
                    placeholder="Client Secret"
                    type="password"
                    value={formData.source?.credentials?.clientSecret || ""}
                    onChange={(e) =>
                      updateCredentials("clientSecret", e.target.value)
                    }
                    required
                  />
                  <textarea
                    className="form-control form-control-sm mb-2"
                    placeholder="Refresh Token"
                    value={formData.source?.credentials?.refreshToken || ""}
                    onChange={(e) =>
                      updateCredentials("refreshToken", e.target.value)
                    }
                    required
                    rows="2"
                  />
                  <div className="row g-2">
                    <div className="col-6">
                      <select
                        className="form-select form-select-sm"
                        value={formData.source?.credentials?.dc || "com"}
                        onChange={(e) =>
                          updateCredentials("dc", e.target.value)
                        }
                      >
                        <option value="com">.com (US)</option>
                        <option value="in">.in (India)</option>
                      </select>
                    </div>
                    <div className="col-6">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Identifier"
                        value={formData.process?.targetIdentifier || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            process: { targetIdentifier: e.target.value },
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12">
                <div className="p-2 border rounded text-center bg-white">
                  <small className="text-muted d-block fs-0625">
                    PREVIEW CRON
                  </small>
                  <code className="text-primary fw-bold">
                    {formData.cronExpression}
                  </code>
                </div>
              </div>

              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className="form-check-label small fw-bold">
                    Active Schedule
                  </label>
                </div>
              </div>

              <div className="col-12 pt-2">
                <button
                  type="submit"
                  className="btn btn-dark w-100 rounded-pill fw-bold py-2 shadow"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? "Saving..." : "Save Configuration"}
                </button>
              </div>
            </div>
          </form>
        </Dialog>
      )}
    </Layout>
  );
};

export default ScheduleSync;
